import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface WorkShiftPricingRule {
  id: number;
  category: number;
  type: number;
  value: number;
  isActive: boolean;
  createdAt: string;
  vigenciaDesde: string | null;
}

export enum WorkShiftPricingRuleCategory {
  HsFija = 0,
  HsEventual = 1,
  HsFeriado50 = 2,
  HsEventFeriado50 = 3,
  HsFeriado100 = 4,
  HsEventFeriado100 = 5,
  EnviosFueraServicio = 6
}

export enum RuleType {
  Fijo = 0,
  PorKm = 1,
  Porcentaje = 2,
  PorTiempo = 3
}

@Component({
  selector: 'app-fixed-point-pricing-rules',
  templateUrl: './fixed-point-pricing-rules.component.html',
  styleUrls: ['./fixed-point-pricing-rules.component.css']
})
export class FixedPointPricingRulesComponent implements OnInit, OnDestroy {
  pricingRules: WorkShiftPricingRule[] = [];
  editForm: FormGroup;
  isEditModalOpen = false;
  currentRule: WorkShiftPricingRule | null = null;
  private destroy$ = new Subject<void>();

  private defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json'
  });

  constructor(
    private http: HttpClient,
    private fb: FormBuilder
  ) {
    this.editForm = this.fb.group({
      value: [0, [Validators.required, Validators.min(0)]],
      vigenciaDesde: [null]
    });
  }

  ngOnInit(): void {
    this.destroy$ = new Subject<void>();
    this.fetchPricingRules();
  }

  fetchPricingRules(): void {
    const url = `${environment.apiBaseUrl}/api/WorkShiftPricingRules/GetAll`;
    this.http.get<WorkShiftPricingRule[]>(url, { headers: this.defaultHeaders })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rules) => {
          this.pricingRules = (rules || []).sort((a, b) => a.category - b.category);
        },
        error: (error) => {
          console.error('Error fetching work shift pricing rules:', error);
        }
      });
  }

  onEdit(rule: WorkShiftPricingRule): void {
    this.currentRule = rule;
    this.editForm.patchValue({
      value: rule.value,
      vigenciaDesde: rule.vigenciaDesde ? rule.vigenciaDesde.substring(0, 10) : null
    });
    this.isEditModalOpen = true;
  }

  closeEditModal(): void {
    this.isEditModalOpen = false;
    this.currentRule = null;
    this.editForm.reset();
  }

  saveRule(): void {
    if (this.editForm.invalid || !this.currentRule) {
      return;
    }

    const formValue = this.editForm.value;
    const payload = {
      category: this.currentRule.category,
      type: this.currentRule.type,
      value: Number(formValue.value),
      vigenciaDesde: formValue.vigenciaDesde ? formValue.vigenciaDesde : null
    };

    const url = `${environment.apiBaseUrl}/api/WorkShiftPricingRules/Post`;
    this.http.post(url, payload, { headers: this.defaultHeaders })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.fetchPricingRules();
          this.closeEditModal();
        },
        error: (error) => {
          console.error('Error saving work shift pricing rule:', error);
        }
      });
  }

  getCategoryDescription(category: number): string {
    const descriptions: { [key: number]: string } = {
      [WorkShiftPricingRuleCategory.HsFija]: 'Hs Fija',
      [WorkShiftPricingRuleCategory.HsEventual]: 'Hs Eventual',
      [WorkShiftPricingRuleCategory.HsFeriado50]: 'Hs Feriado 50%',
      [WorkShiftPricingRuleCategory.HsEventFeriado50]: 'Hs Event. Feriado 50%',
      [WorkShiftPricingRuleCategory.HsFeriado100]: 'Hs Feriado 100%',
      [WorkShiftPricingRuleCategory.HsEventFeriado100]: 'Hs Event. Feriado 100%',
      [WorkShiftPricingRuleCategory.EnviosFueraServicio]: 'Envíos fuera del servicio'
    };
    return descriptions[category] || String(category);
  }

  getIcon(category: number): string {
    const icons: { [key: number]: string } = {
      [WorkShiftPricingRuleCategory.HsFija]: '🕐',
      [WorkShiftPricingRuleCategory.HsEventual]: '🕐',
      [WorkShiftPricingRuleCategory.HsFeriado50]: '🎉',
      [WorkShiftPricingRuleCategory.HsEventFeriado50]: '🎉',
      [WorkShiftPricingRuleCategory.HsFeriado100]: '🎊',
      [WorkShiftPricingRuleCategory.HsEventFeriado100]: '🎊',
      [WorkShiftPricingRuleCategory.EnviosFueraServicio]: '📦'
    };
    return icons[category] || '📋';
  }

  formatValue(rule: WorkShiftPricingRule): string {
    if (rule.value === null || rule.value === undefined) {
      return '';
    }
    return `$${rule.value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
